import type { Member, TeamMatchPlayResults, CurrentRyderCupTeamMember } from '../types/api.types';

export interface MatchSummary {
  won: number;
  lost: number;
  halved: number;
}

/**
 * Calculates match summary statistics for a specific player from a list of matches
 */
export const calculateMatchListSummary = (matchList: TeamMatchPlayResults[], memberId: string | number): MatchSummary =>
  matchList.reduce<MatchSummary>(
    (sum, match) => {
      if (match.winningTeam === 'AS') {
        return { won: sum.won, lost: sum.lost, halved: sum.halved + 1 };
      } else if (
        match.winningTeam === match.teamA ? match.teamAMember1 === memberId : match.teamBMember1 === memberId
      ) {
        return { won: sum.won + 1, lost: sum.lost, halved: sum.halved };
      } else {
        return { won: sum.won, lost: sum.lost + 1, halved: sum.halved };
      }
    },
    { won: 0, lost: 0, halved: 0 }
  );

/**
 * Calculates overall match summary for a player against all opponents
 */
export const calculatePlayer1MatchSummary = (
  mapSinglesStrokeResults: Record<string | number, Record<string | number, TeamMatchPlayResults[]>>,
  player1Id: string | number
): MatchSummary | undefined => {
  const player1Results = mapSinglesStrokeResults?.[player1Id];
  if (!player1Results) return undefined;

  return Object.entries(player1Results).reduce(
    (sum, [, matches]) => {
      const opponentSummary = calculateMatchListSummary(matches, player1Id);
      return {
        won: sum.won + opponentSummary.won,
        lost: sum.lost + opponentSummary.lost,
        halved: sum.halved + opponentSummary.halved,
      };
    },
    { won: 0, lost: 0, halved: 0 }
  );
};

/**
 * Calculates head-to-head match summary between two specific players
 */
export const calculatePlayer2MatchSummary = (
  player1Results: Record<string | number, TeamMatchPlayResults[]> | undefined,
  player2Id: string | number,
  player1Id: string | number
): MatchSummary | undefined => {
  const matchList = player1Results?.[player2Id];
  return matchList ? calculateMatchListSummary(matchList, player1Id) : undefined;
};

/**
 * Determines the winning player from a match result
 */
export const getWinningPlayer = (result: TeamMatchPlayResults): string | number | null => {
  return result.winningTeam === result.teamA ? result.teamAMember1 : result.teamBMember1;
};

/**
 * Filters and sorts opponent IDs based on Ryder Cup team selection
 */
export const getFilteredOpponentIds = (params: {
  allOpponentIds: string[];
  showLatestRyderCupOnly: boolean;
  selectedMemberId: string | number | null;
  latestRyderCupTeams: CurrentRyderCupTeamMember[];
  player1Results: Record<string | number, TeamMatchPlayResults[]> | undefined;
}): string[] => {
  const { allOpponentIds, showLatestRyderCupOnly, selectedMemberId, latestRyderCupTeams, player1Results } = params;

  if (!showLatestRyderCupOnly || !selectedMemberId) {
    return allOpponentIds;
  }

  // Find the selected member's team
  const selectedMemberTeam = latestRyderCupTeams.find((m) => m.id === selectedMemberId)?.team;
  if (!selectedMemberTeam) {
    return allOpponentIds;
  }

  // Get the opposing team
  const opposingTeam = selectedMemberTeam === 'USA' ? 'EU' : 'USA';
  const opposingTeamMembers = latestRyderCupTeams.filter((m) => m.team === opposingTeam);

  // Create a map of match counts for sorting
  const matchCountMap = new Map<string, number>();
  allOpponentIds.forEach((id) => {
    if (player1Results?.[id]) {
      matchCountMap.set(id, player1Results[id].length);
    }
  });

  // Sort opposing team members by match count
  const membersWithMatches = opposingTeamMembers
    .filter((m) => matchCountMap.has(m.id))
    .sort((a, b) => (matchCountMap.get(b.id) || 0) - (matchCountMap.get(a.id) || 0))
    .map((m) => m.id);

  const membersWithoutMatches = opposingTeamMembers
    .filter((m) => !matchCountMap.has(m.id))
    .sort((a, b) => {
      const aIsCaptain = a.captain === 'Yes';
      const bIsCaptain = b.captain === 'Yes';
      if (aIsCaptain !== bIsCaptain) {
        return aIsCaptain ? -1 : 1;
      }
      return a.id.localeCompare(b.id);
    })
    .map((m) => m.id);

  return [...membersWithMatches, ...membersWithoutMatches];
};

/**
 * Gets the total match count for a member
 */
const getMemberMatchCount = (
  memberId: string | number,
  mapSinglesStrokeResults: Record<string | number, Record<string | number, TeamMatchPlayResults[]>>
): number => {
  const memberResults = mapSinglesStrokeResults?.[memberId];
  if (!memberResults) return 0;
  return Object.values(memberResults).reduce((sum, matches) => sum + matches.length, 0);
};

/**
 * Compares two members by team (USA first, then EU)
 * Returns 0 if teams are the same or if Ryder Cup data is missing
 */
const compareByTeam = (
  aRyderCup: CurrentRyderCupTeamMember | undefined,
  bRyderCup: CurrentRyderCupTeamMember | undefined
): number => {
  if (!aRyderCup || !bRyderCup || aRyderCup.team === bRyderCup.team) return 0;
  return aRyderCup.team === 'USA' ? -1 : 1;
};

/**
 * Compares two members by captain status (captains first)
 * Returns 0 if both are captains or both are not captains
 */
const compareByCaptain = (
  aRyderCup: CurrentRyderCupTeamMember | undefined,
  bRyderCup: CurrentRyderCupTeamMember | undefined
): number => {
  if (!aRyderCup || !bRyderCup) return 0;
  const aIsCaptain = aRyderCup.captain === 'Yes';
  const bIsCaptain = bRyderCup.captain === 'Yes';
  if (aIsCaptain === bIsCaptain) return 0;
  return aIsCaptain ? -1 : 1;
};

/**
 * Sorts members list by team, captain status, and match count
 */
export const getSortedMembers = (
  members: Member[],
  showLatestRyderCupOnly: boolean,
  latestRyderCupTeams: CurrentRyderCupTeamMember[],
  mapSinglesStrokeResults: Record<string | number, Record<string | number, TeamMatchPlayResults[]>>
): Member[] => {
  return [...members].sort((a, b) => {
    if (showLatestRyderCupOnly) {
      const aRyderCup = latestRyderCupTeams.find((m) => m.id === a.id);
      const bRyderCup = latestRyderCupTeams.find((m) => m.id === b.id);

      // Sort by team (USA first, then EU)
      const teamComparison = compareByTeam(aRyderCup, bRyderCup);
      if (teamComparison !== 0) return teamComparison;

      // Within same team, sort by captain (captains first)
      const captainComparison = compareByCaptain(aRyderCup, bRyderCup);
      if (captainComparison !== 0) return captainComparison;
    }

    // Sort by match count (most matches first)
    const aMatchCount = getMemberMatchCount(a.id, mapSinglesStrokeResults);
    const bMatchCount = getMemberMatchCount(b.id, mapSinglesStrokeResults);
    return bMatchCount - aMatchCount;
  });
};

/**
 * Gets the shots handicap text for a match result
 */
export const getShotsText = (
  result: TeamMatchPlayResults,
  teamAMemberName: string,
  teamBMemberName: string
): string => {
  const shotsGiven = result.shotsGivenByTeamB || 0;
  if (shotsGiven > 0) {
    return `${teamBMemberName} gave ${teamAMemberName} ${shotsGiven} shots`;
  } else {
    return `${teamAMemberName} gave ${teamBMemberName} ${-shotsGiven} shots`;
  }
};

/**
 * Enriched match result with opponent ID for display
 */
export interface EnrichedMatchResult extends TeamMatchPlayResults {
  opponentId: string | number;
}

/**
 * Filters match results based on selected opponent, or returns all matches with opponent IDs
 */
export const getFilteredResults = (
  selectedMember: string | number | null,
  selectedOpponent: string | number | null,
  player1Results: Record<string | number, TeamMatchPlayResults[]> | undefined
): EnrichedMatchResult[] | undefined => {
  if (!selectedMember || !player1Results) return undefined;

  if (selectedOpponent != null) {
    // Return matches against specific opponent
    const matches = player1Results[selectedOpponent];
    if (!matches) return undefined;
    return matches.map((match) => ({ ...match, opponentId: selectedOpponent }));
  }

  // Return all matches with opponent IDs
  return Object.entries(player1Results).flatMap(([opponentId, matches]) =>
    matches.map((match) => ({ ...match, opponentId }))
  );
};

/**
 * Sorts match results by year (most recent first)
 */
export const sortMatchResultsByYear = (results: EnrichedMatchResult[]): EnrichedMatchResult[] => {
  return [...results].sort((a, b) => {
    const yearA = a.eventID ? parseInt(a.eventID.substring(0, 4)) : 0;
    const yearB = b.eventID ? parseInt(b.eventID.substring(0, 4)) : 0;
    return yearB - yearA; // Most recent first
  });
};
